import type { IBusinessUser } from '@/domain/entities/user';
import type { RootState } from '@/store/root-state';
import { createSelector } from '@reduxjs/toolkit';
import { selectCurrentUserProfile } from '@/store/selectors/authorizationSelectors';

function hasText(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

export function isProfileComplete(u: IBusinessUser | null | undefined): boolean {
  if (!u) return false;

  return (
    hasText(u.firstName) &&
    hasText(u.lastName) &&
    hasText(u.phone) &&
    !!u.address &&
    hasText(u.address.line1) &&
    hasText(u.address.city) &&
    hasText(u.address.region) &&
    hasText(u.address.postalCode) &&
    (u.address.country === 'US' || u.address.country === 'CA')
  );
}

export function isPhoneVerified(u: IBusinessUser | null | undefined): boolean {
  // keep boolean for now; later you can store phoneVerifiedAt: number
  return !!u?.phoneVerified;
}

export type OnboardingStepKey = 'profile' | 'phone' | 'org';

export function getOnboardingStep(
  u: IBusinessUser | null | undefined,
  hasActiveOrgMembership: boolean = true,
): OnboardingStepKey | null {
  if (!isProfileComplete(u)) return 'profile';
  if (!isPhoneVerified(u)) return 'phone';
  if (!hasActiveOrgMembership) return 'org';
  return null;
}

export function needsOnboarding(
  profile: IBusinessUser | null | undefined,
  hasActiveOrgMembership: boolean = true,
): boolean {
  return getOnboardingStep(profile, hasActiveOrgMembership) !== null;
}

export const selectUserProfile = (s: RootState) => selectCurrentUserProfile(s);
export const selectOnboardingStep = createSelector(selectCurrentUserProfile, (profile) => getOnboardingStep(profile));
export const selectNeedsOnboarding = createSelector(selectOnboardingStep, (step) => step !== null);
